import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, Handshake, Users, ShieldCheck, FileText, Clock, Award } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LIBRARY_IMAGE } from "@/lib/content";
import SEO from "@/components/SEO";
import { breadcrumbSchema, faqPageSchema } from "@/lib/schemas";

const ADVANTAGES = [
  {
    icon: Handshake,
    title: "Your Client Relationship Stays Yours",
    body: "We advise on the legal and tax dimensions of the matter — you remain the primary financial or accounting relationship. No poaching, no client capture, no confusion at the family table.",
  },
  {
    icon: Users,
    title: "One Desk — Attorney and CPA",
    body: "Darryl V. Pratt is dually licensed as a Texas attorney and CPA. Referred matters get integrated legal + tax analysis from a single senior advisor. No sub-referral to a tax specialist.",
  },
  {
    icon: ShieldCheck,
    title: "Discretion by Covenant",
    body: "Nothing about the referred client, transaction, or family situation is shared, published, or used in marketing. Your client's affairs remain private within a small credentialed circle.",
  },
  {
    icon: Clock,
    title: "Priority Response",
    body: "Referred matters receive same-week intake and a preliminary conference within ten business days. Time-sensitive matters (closings, sunsets, transactions) move faster still.",
  },
  {
    icon: FileText,
    title: "Coordinated Deliverables",
    body: "Draft documents, engagement letters, and closing binders are furnished to both client and referrer (with client consent) so your file remains complete and your workflow uninterrupted.",
  },
  {
    icon: Award,
    title: "A Reference Standard",
    body: "Every referred matter reflects on you. Our engagement letters, drafting, and follow-through are held to a standard that makes referring us an act of professional confidence, not a risk.",
  },
];

const REFERRAL_TYPES = [
  {
    kind: "For CPAs & Financial Planners",
    matters: [
      "Business succession planning and buy-sell agreements",
      "Trust structures for tax-advantaged wealth transfer",
      "Asset protection planning for high-liability professions",
      "Estate planning coordination with existing investment plans",
      "Pre-immigration tax planning for foreign national clients",
      "Chapter 7 / 11 restructuring for over-leveraged clients",
    ],
  },
  {
    kind: "For Bankers, Wealth Advisors & Family Offices",
    matters: [
      "Multi-generational trust architecture",
      "Family office governance and entity structuring",
      "Fiduciary review of trustees and executors",
      "Complex real estate and business ownership transitions",
      "Legacy governance — family constitutions and heir councils",
      "Coordinated cross-border planning (U.S.–India, U.K., E.U.)",
    ],
  },
];

const HOW_IT_WORKS = [
  {
    no: "01",
    title: "Warm Introduction",
    body: "You send a brief email or make a call — client name, matter type, and any urgency. If preferred, we furnish a template introduction letter you can send in your own voice.",
  },
  {
    no: "02",
    title: "Same-Week Intake",
    body: "Darryl personally reviews the referral within two business days. If a matter is time-sensitive, we make room the same week for a conference.",
  },
  {
    no: "03",
    title: "Complimentary Conference",
    body: "The first hour of counsel is on the firm. The client meets with Darryl, scope is defined, and — if there's mutual fit — an engagement letter is issued.",
  },
  {
    no: "04",
    title: "Ongoing Coordination",
    body: "You are copied on relevant deliverables (with client consent). We coordinate with your firm on tax filings, financial statements, or investment accounts affected by the matter.",
  },
  {
    no: "05",
    title: "Client Returns to You",
    body: "Legal matter closed. Trust funded. Deal closed. Client returns to your ongoing advisory relationship — enhanced, not disrupted.",
  },
];

const ADVISOR_FAQ = [
  {
    q: "Do you pay referral fees?",
    a: "Texas Disciplinary Rule 1.04(f) prohibits fee-sharing between lawyers and non-lawyers for legal work. We do not — and cannot — pay referral fees to CPAs, financial planners, bankers, or other non-lawyer professionals. Instead, we invest in the relationship: complimentary CLE for your firm, co-branded educational materials, priority scheduling for your clients, and the assurance that referred matters are handled to a standard you can stand behind.",
  },
  {
    q: "Will you try to move my client's tax, financial planning, or advisory work to your firm?",
    a: "No. Darryl is licensed as a CPA, but this firm's practice is legal counsel. We do not prepare tax returns, manage investments, or act as a financial planner. When a referred client asks 'should I move my accounting or advisory work?' — we redirect them back to you. Every time.",
  },
  {
    q: "What matter types do you accept?",
    a: "Business law and transactions, business succession, physician and professional-practice matters, franchise structuring, trust and estate planning, asset protection, probate and trust administration, guardianship, elder law, Chapter 7 and Chapter 11 bankruptcy restructuring, and cross-border U.S.–India / South Asian family matters. We do not handle personal injury, family law (divorce/custody), criminal defense, or immigration outside of pre-immigration tax planning.",
  },
  {
    q: "How do you handle sensitive family or business situations?",
    a: "With discretion as a covenant of the engagement. Nothing about the referred matter is published, shared with third parties, or used in marketing. Referring advisors are furnished with a Non-Disclosure Acknowledgment on request.",
  },
  {
    q: "Can you provide a co-branded introduction letter for my client?",
    a: "Yes. On request, we furnish a template introduction letter you can personalize in your own voice — explaining who we are, why you selected us, and what the client can expect from the first conference. Some referring advisors prefer to make a direct verbal handoff; others prefer written introduction. Both are welcome.",
  },
  {
    q: "How do you handle conflicts with my client's existing counsel or accountant?",
    a: "We do not solicit or displace incumbent professionals. If the referred client already has counsel or accounting relationships, we coordinate with them on scope. If a client asks us to review the work of a prior advisor, we will do so with professional courtesy and only for the client's benefit.",
  },
];

const ROLES = [
  { key: "cpa", label: "CPA" },
  { key: "financial_planner", label: "Financial Planner / Wealth Advisor" },
  { key: "banker", label: "Banker / Private Banker" },
  { key: "family_office", label: "Family Office" },
  { key: "insurance", label: "Insurance Professional" },
  { key: "other_advisor", label: "Other Trusted Advisor" },
];

const URGENCY_LEVELS = [
  "Exploratory · Client is still deciding",
  "Standard · Within 30 days is fine",
  "Priority · Within 2 weeks preferred",
  "Time-sensitive · Closing, transaction, or deadline this week",
];

export default function ForAdvisors() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    role: "",
    firm_name: "",
    location: "",
    timeline: "",
    message: "",
    preferred_contact: "email",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit =
    form.full_name.trim() &&
    form.email.trim() &&
    form.role &&
    form.message.trim().length >= 15;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/intake", {
        ...form,
        practice_area: "Advisor Referral",
        stage: "formation",
        matter_class: "advisor_referral",
        referral_source: form.firm_name,
        estimated_value: form.timeline,
      });
      setDone(true);
      toast.success("Referral received. Thank you.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-ivory" data-testid="for-advisors-page">
      <SEO
        title="For Trusted Advisors — CPA & Financial Planner Referrals · Lone Star Counsel"
        description="A referral-partner portal for CPAs, financial planners, bankers, and family offices in Texas. Coordinated legal + tax counsel that keeps your client relationship intact."
        path="/for-advisors"
        schema={[
          faqPageSchema(ADVISOR_FAQ),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "For Trusted Advisors", path: "/for-advisors" },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="relative panel-navy min-h-[70vh] flex items-end" data-testid="advisors-hero">
        <div className="absolute inset-0">
          <img src={LIBRARY_IMAGE} alt="Private library" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/55 to-navy/95" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-32 md:py-40 w-full">
          <div className="kicker kicker-ivory">For Trusted Advisors</div>
          <div className="mt-3 text-[11px] uppercase tracking-[0.42em] text-brass font-medium">
            CPAs <span className="text-brass/50 mx-2">·</span>
            Financial Planners <span className="text-brass/50 mx-2">·</span>
            Bankers <span className="text-brass/50 mx-2">·</span>
            Family Offices
          </div>
          <h1 className="mt-8 font-display text-ivory text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
            Refer with <br/>
            <span className="italic font-cormorant text-brass">confidence.</span>
          </h1>
          <p className="mt-8 max-w-2xl font-cormorant italic text-2xl md:text-3xl text-ivory/85 leading-snug">
            A referral-partner portal for the CPAs, financial planners, bankers, and family
            offices who trust us with their most important clients.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#refer" className="btn-brass" data-testid="advisors-refer-cta">
              Refer a Client →
            </a>
            <a href="#how-it-works" className="btn-ghost-ivory" data-testid="advisors-learn-cta">
              How It Works
            </a>
          </div>
        </div>
      </section>

      {/* THE ONE-DESK PROMISE */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="advisors-promise">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="kicker">The One-Desk Advantage</div>
            <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[1.0]">
              Attorney and CPA.<br/>
              <span className="italic font-cormorant text-oxblood">One senior counsel.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-lg md:text-xl font-light text-slate2 leading-relaxed space-y-6">
            <p className="dropcap">
              When you refer a client for a legal matter with tax implications — or a tax matter
              with legal consequences — most referral chains split the work between an attorney
              and a separate tax specialist. The client repeats their story twice. Advice
              contradicts. Nothing coordinates.
            </p>
            <p>
              Darryl V. Pratt is dually licensed as a Texas attorney and a Certified Public
              Accountant, with nearly 30 years of practice across both disciplines. Your
              referred client receives integrated legal and tax analysis from a single senior
              advisor — with your existing accounting or financial planning relationship left
              exactly where it belongs: with you.
            </p>
          </div>
        </div>
      </section>

      {/* SIX ADVANTAGES */}
      <section className="bg-linen py-24 md:py-32" data-testid="advisors-advantages">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <div className="kicker">What Referring Advisors Receive</div>
            <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98]">
              Six things you can<br/>
              <span className="italic font-cormorant text-oxblood">count on.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
            {ADVANTAGES.map((a, i) => {
              const Icon = a.icon;
              return (
                <article key={i} className="hairline-top pt-6" data-testid={`advantage-${i}`}>
                  <div className="text-brass"><Icon size={26} strokeWidth={1.25} /></div>
                  <h3 className="mt-6 font-display text-2xl md:text-[1.6rem] text-navy leading-[1.15]">{a.title}</h3>
                  <p className="mt-4 text-base text-slate2 font-light leading-relaxed">{a.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* REFERRAL TYPES BY DISCIPLINE */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="advisors-referral-types">
        <div className="max-w-3xl mb-14">
          <div className="kicker">Common Referrals</div>
          <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
            What we routinely<br/>
            <span className="italic font-cormorant text-oxblood">handle for referred clients.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
          {REFERRAL_TYPES.map((group, i) => (
            <div key={group.kind} data-testid={`referral-type-${i}`}>
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">{group.kind}</div>
              <ul className="mt-6 space-y-4">
                {group.matters.map((m, j) => (
                  <li key={j} className="flex items-start gap-3 hairline-top pt-4" data-testid={`referral-item-${i}-${j}`}>
                    <Check size={16} className="text-brass shrink-0 mt-1.5" />
                    <span className="font-cormorant text-lg md:text-xl text-navy leading-snug">{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS — 5 STEPS */}
      <section id="how-it-works" className="panel-navy py-24 md:py-32" data-testid="advisors-process">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="kicker kicker-ivory">How It Works</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02] text-ivory">
                From referral<br/>
                <span className="italic font-cormorant text-brass">to closed matter.</span>
              </h2>
              <p className="mt-8 font-cormorant italic text-lg text-ivory/85 leading-snug max-w-md">
                The client returns to your ongoing advisory relationship — enhanced, not disrupted.
              </p>
            </div>
            <ol className="lg:col-span-7 lg:col-start-6 space-y-10">
              {HOW_IT_WORKS.map((s, i) => (
                <li key={s.no} className="grid grid-cols-12 gap-6 items-baseline hairline-top pt-8" data-testid={`process-step-${i}`}>
                  <div className="col-span-2 font-cormorant italic text-4xl md:text-5xl text-brass">{s.no}</div>
                  <div className="col-span-10">
                    <h3 className="font-display text-2xl md:text-3xl text-ivory leading-[1.1]">{s.title}</h3>
                    <p className="mt-3 text-base md:text-lg font-light text-ivory/80 leading-relaxed">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* FAQ FOR ADVISORS */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="advisors-faq">
        <div className="mb-14">
          <div className="kicker">Frequently Asked</div>
          <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
            Questions from<br/>
            <span className="italic font-cormorant text-oxblood">referring advisors.</span>
          </h2>
        </div>
        <div className="space-y-10">
          {ADVISOR_FAQ.map((f, i) => (
            <article key={i} className="hairline-top pt-8" data-testid={`advisor-faq-${i}`}>
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">Question {String(i + 1).padStart(2, "0")}</div>
              <h3 className="mt-4 font-display text-2xl md:text-3xl text-navy leading-[1.15]">{f.q}</h3>
              <p className="mt-5 text-base md:text-lg font-light text-slate2 leading-relaxed">{f.a}</p>
            </article>
          ))}
        </div>
      </section>

      {/* REFERRAL FORM */}
      <section id="refer" className="bg-linen py-24 md:py-32" data-testid="advisors-form-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="kicker">Send a Referral</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
                Refer a<br/>
                <span className="italic font-cormorant text-oxblood">client privately.</span>
              </h2>
              <p className="mt-8 text-slate2 font-light leading-relaxed">
                A brief note tells us what your client needs. Darryl will respond personally within
                two business days — and a template introduction letter is furnished on request.
              </p>
              <div className="mt-10 hairline-top pt-8">
                <p className="font-cormorant italic text-lg text-slate2/80 leading-snug">
                  Prefer to call? The firm&apos;s direct advisor line is answered by Darryl personally
                  between 9 a.m. and 5 p.m. Central. Written referrals are archived with your file
                  for reference.
                </p>
              </div>
            </div>

            {done ? (
              <div className="lg:col-span-7 lg:col-start-6" data-testid="advisors-success">
                <div className="kicker">Received</div>
                <h3 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02]">
                  Referral received.<br/>
                  <span className="italic font-cormorant text-oxblood">Thank you.</span>
                </h3>
                <p className="mt-8 text-lg text-slate2 font-light leading-relaxed">
                  Darryl will review your referral personally and respond within two business days.
                  If you requested a template introduction letter, it will accompany the reply.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/" className="btn-ghost" data-testid="advisors-return-home">Return Home</Link>
                  <Link to="/journal" className="btn-ghost" data-testid="advisors-view-journal">Read the Journal</Link>
                </div>
              </div>
            ) : (
              <form className="lg:col-span-7 lg:col-start-6 space-y-8" onSubmit={submit} data-testid="advisors-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="Your Full Name" required>
                    <input className="field" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="advisor-full-name" />
                  </Field>
                  <Field label="Email Address" required>
                    <input className="field" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="advisor-email" />
                  </Field>
                  <Field label="Telephone">
                    <input className="field" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="advisor-phone" />
                  </Field>
                  <Field label="Firm Name">
                    <input className="field" value={form.firm_name} onChange={(e) => update("firm_name", e.target.value)} data-testid="advisor-firm-name" />
                  </Field>
                </div>

                <Field label="Your Role" required>
                  <div className="flex flex-wrap gap-2" data-testid="advisor-role-group">
                    {ROLES.map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => update("role", r.key)}
                        data-testid={`advisor-role-${r.key}`}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition ${
                          form.role === r.key
                            ? "bg-oxblood text-ivory border-oxblood"
                            : "bg-ivory border-transparent hover:border-oxblood"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="Your City/State">
                    <input className="field" value={form.location} onChange={(e) => update("location", e.target.value)} data-testid="advisor-location" placeholder="Frisco, Texas" />
                  </Field>
                  <Field label="Client Timeline / Urgency">
                    <select className="field" value={form.timeline} onChange={(e) => update("timeline", e.target.value)} data-testid="advisor-timeline">
                      <option value="">Select timing…</option>
                      {URGENCY_LEVELS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </Field>
                </div>

                <Field
                  label="Briefly describe the client and matter"
                  required
                >
                  <textarea
                    className="field"
                    rows={7}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    data-testid="advisor-message"
                    placeholder="No confidential client identifying information required at this stage. A brief description of the matter type (business succession, trust structure, restructuring, cross-border, etc.) and any relevant context is sufficient."
                  />
                </Field>

                <Field label="Preferred Contact for Response">
                  <select className="field" value={form.preferred_contact} onChange={(e) => update("preferred_contact", e.target.value)} data-testid="advisor-preferred-contact">
                    <option value="email">By Email</option>
                    <option value="phone">By Telephone</option>
                  </select>
                </Field>

                <div className="hairline pt-8 flex flex-wrap items-center justify-between gap-6">
                  <p className="text-xs text-slate2/70 font-cormorant italic max-w-md leading-snug">
                    Submitting this form does not create an attorney–client relationship. It is a
                    professional referral inquiry, reviewed personally, and treated in strict confidence.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting || !canSubmit}
                    className="btn-brass disabled:opacity-40 inline-flex items-center gap-2"
                    data-testid="advisor-submit"
                  >
                    {submitting ? "Submitting…" : (<>Send Referral <ArrowRight size={16} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="field-label mb-2">
        {label} {required && <span className="text-oxblood">·</span>}
      </div>
      {children}
    </label>
  );
}
